import { css } from '@emotion/css';
import AddIcon from '@mui/icons-material/Add';
import Check from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import { CurrencyField } from '@src/clients/financialSettings/fields/CurrencyFields';
import { Button } from '@src/common/components/button/Button';
import type { RowDefinition } from '@src/common/components/SimpleTable';
import { Stack } from '@src/common/components/Stack';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
} from '@src/common/components/Table';
import { TableRow } from '@src/common/components/TableRow';
import { Typography } from '@src/common/components/Typography';
import { theme } from '@src/common/styles/Theme';
import type { AdditionalFee } from '@src/orders/domain/ReviewableOrder';
import { AddFeeForm } from '@src/orders/review/AddFeeForm';
import type { FeesFormFields } from '@src/orders/schemas/ReviewOrderFormPermitFields';
import {
  AmountField,
  DescriptionField,
  TaxableField,
} from '@src/orders/schemas/ReviewOrderFormPermitFields';
import { useState } from 'react';
import type {
  ArrayPath,
  Control,
  FieldArray,
  Path,
  UseFormGetValues,
} from 'react-hook-form';
import { useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface Props<T extends FeesFormFields> {
  control: Control<T>;
  getValues: UseFormGetValues<T>;
  readonly: boolean;
  onFeesChanged: () => void;
}

export const AdditionalFees = <T extends FeesFormFields>({
  control,
  getValues,
  readonly,
  onFeesChanged,
}: Props<T>) => {
  const { t } = useTranslation();
  const [showAddFeeModal, setShowAddFeeModal] = useState(false);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'additionalFees' as ArrayPath<T>,
  });

  const addFee = (data: AdditionalFee) => {
    append(data as FieldArray<T, ArrayPath<T>>);
    setShowAddFeeModal(false);
    onFeesChanged();
  };

  const columnsName = [
    { name: t('fields.description'), align: 'left' },
    { name: t('reviewPermit.taxable'), align: 'left' },
    { name: t('reviewPermit.currency'), align: 'left' },
    { name: t('reviewPermit.fees'), align: 'right' },
  ];

  const getReadOnlyValue = (key: string, fieldName: string) => (
    <Typography key={key}>{getValues(fieldName as Path<T>)}</Typography>
  );

  const getTaxableValue = (key: string, fieldName: string) => {
    const isTaxable = getValues(fieldName as Path<T>);
    return (
      <>
        {isTaxable && (
          <Stack direction="row" spacing={1}>
            <Check style={{ color: theme.palette.success.success }} />
            <Typography key={key}>{t('yes')}</Typography>
          </Stack>
        )}
        {!isTaxable && (
          <Stack direction="row" spacing={1}>
            <ClearIcon color="error" />
            <Typography key={key}>{t('no')}</Typography>
          </Stack>
        )}
      </>
    );
  };

  const getReadOnlyAmountValue = (
    key: string,
    fieldName: string,
    index: number,
  ) => (
    <Stack direction="row" justifyContent="end">
      <Typography key={key}>$ {getValues(fieldName as Path<T>)}</Typography>
      {!readonly && (
        <ClearIcon
          aria-label={t('reviewPermit.removeOtherFee')}
          color="error"
          cursor="pointer"
          onClick={() => {
            remove(index);
            onFeesChanged();
          }}
        />
      )}
    </Stack>
  );

  const rows: RowDefinition[] = fields.map((fee, index) => ({
    id: `additional-fee-${fee.id}`,
    columnsContent: [
      getReadOnlyValue(
        `${fee.id}-description`,
        `additionalFees.${index}.${DescriptionField.fieldName}`,
      ),
      getTaxableValue(
        `${fee.id}-taxable`,
        `additionalFees.${index}.${TaxableField.fieldName}`,
      ),
      getReadOnlyValue(
        `${fee.id}-currency`,
        `additionalFees.${index}.${CurrencyField.fieldName}`,
      ),
      getReadOnlyAmountValue(
        `${fee.id}-amount`,
        `additionalFees.${index}.${AmountField.fieldName}`,
        index,
      ),
    ],
  }));

  return (
    <>
      <Typography variant="subtitle1" style={{ padding: theme.spacing['2'] }}>
        {t('reviewPermit.otherFees')}
      </Typography>

      <Table size="small" aria-label={t('reviewPermit.otherFees')}>
        <TableHead>
          <TableRow>
            {columnsName.map((column) => (
              <TableCell
                key={column.name}
                className={cellStyle(column)}
                sx={{
                  borderBottom: theme.table.border.secondary,
                  borderTop: theme.table.border.primary,
                }}
              >
                {column.name}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.map(({ id, columnsContent }) => (
            <TableRow key={`row-${id}`} hover>
              {columnsContent.map((content, i) => (
                <TableCell
                  key={`row-${id}-column-${i}`}
                  className={cellStyle(columnsName[i])}
                >
                  {content}
                </TableCell>
              ))}
            </TableRow>
          ))}

          <TableRow key="row-add-other-fees">
            <TableCell key="col-add-other-fees">
              <Button
                disabled={readonly}
                type="button"
                size="small"
                onClick={() => setShowAddFeeModal(true)}
                startIcon={<AddIcon />}
              >
                {t('reviewPermit.otherFees')}
              </Button>
            </TableCell>
            <TableCell />
            <TableCell />
          </TableRow>
        </TableBody>
      </Table>

      <AddFeeForm
        open={showAddFeeModal}
        onAdd={addFee}
        onClose={() => setShowAddFeeModal(false)}
      />
    </>
  );
};

const cellStyle = ({ align }: { align: string }) => css`
  min-width: 160px;
  text-align: ${align};
  text-wrap: nowrap;

  &:first-of-type {
    width: 100%;
  }

  &:not(:last-child) {
    border-right: 1px solid ${theme.palette.surface.outlineVariant};
  }
`;
